import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { LogIn, ShieldCheck } from 'lucide-react';
import { motion } from 'motion/react';

import { useNavigate } from 'react-router-dom';

export const Login: React.FC = () => {
  const { signIn, signOut, user, loading: authLoading, isProcessing: authProcessing } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // Gestione parametri di reset/logout per AI Studio
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('logout') || params.has('error_reset')) {
      localStorage.clear();
      sessionStorage.clear();
    }
  }, []);

  // Redirect if already authenticated
  React.useEffect(() => {
    const canNavigate = !!user && !authLoading && !authProcessing;
    
    if (canNavigate) {
      navigate('/user', { replace: true });
    }
  }, [user, authLoading, authProcessing, navigate]);

  const handleLogin = async () => {
    setError(null);
    
    try {
      await signIn();
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Errore durante l\'accesso');
    }
  };

  const handleReload = async () => {
    await signOut();
  };

  const isProcessing = authProcessing || authLoading;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-[380px] w-full bg-white rounded-3xl shadow-xl border px-2 py-10 space-y-8"
      >
        <div className="text-center space-y-2 px-4">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mx-auto mb-4">
            <img 
              src="/images/gufetto.webp" 
              alt="Logo Gesvol" 
              className="w-full h-full object-contain p-2"
              referrerPolicy="no-referrer" 
            />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Gesvol CPGEV</h1>
          <p className="text-muted-foreground">Gestione Volontari Corpo Provinciale GEV</p>
        </div>

        <div className="space-y-4">
          <p className="text-sm text-center text-muted-foreground">
            Accedi esclusivamente con il tuo account istituzionale <strong>@gevbologna.org</strong>
          </p>

          <div className="px-4">
            <button
              onClick={handleLogin}
              disabled={isProcessing}
              className="w-full flex items-center justify-center space-x-3 bg-white border-2 border-slate-200 hover:border-primary/50 hover:bg-slate-50 transition-all p-4 rounded-2xl font-medium disabled:opacity-50"
            >
              {isProcessing ? (
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <img src="https://www.google.com/favicon.ico" className="w-5 h-5" alt="Google" />
                  <span>Accedi con Google</span>
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="px-4">
              <p className="text-sm text-destructive text-center font-medium bg-destructive/10 p-3 rounded-xl">
                {error}
              </p>
            </div>
          )}

          <div className="px-4 pt-4 border-t border-slate-100">
            <button
              onClick={handleReload}
              className="w-full text-xs text-slate-500 hover:text-primary transition-colors font-medium underline underline-offset-4"
            >
              Ricarica
            </button>
          </div>
        </div>

        <div className="pt-4 text-center">
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} CPGEV Bologna. Tutti i diritti riservati.
          </p>
        </div>
      </motion.div>
    </div>
  );
};
